import * as React from 'react';
import { Button, Collapse, Theme, createStyles, makeStyles } from '@material-ui/core';
import AddCommentOutlinedIcon from '@material-ui/icons/AddCommentOutlined';
import { MatterTerminology } from '../../MatterTerminology';
import { MatterSearchField } from '../MatterSearchField';
import { MessageInputCard } from './MessageInputCard';
import { MessageStream } from './MessageStream';
import { Message } from './hooks/useMessageQuery';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        },
        searchRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            paddingBottom: theme.spacing(2.5),
            marginBottom: theme.spacing(2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        searchField: {
            flex: 1,
        },
        streamContainer: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
        },
    })
);

interface MatterMessageViewProps {
    id: string;
    sourceSystem: string;
    showNewMessage?: boolean;
    isUserInternal: boolean;
}

interface MatterMessageViewInnerProps extends MatterMessageViewProps {
    classes: ReturnType<typeof useStyles>;
}

interface MessageViewState {
    showNewMessage?: boolean;
    searchQuery: string;
    latestMessage?: Message;
}

class MatterMessageViewInner extends React.Component<MatterMessageViewInnerProps, MessageViewState>  {

    // tslint:disable-next-line:no-any
    constructor(props: MatterMessageViewInnerProps, context?: any) {
        super(props, context);
        this.state = {
            searchQuery: '',
            showNewMessage: this.props.showNewMessage ? true : false,
        };
        this.toggleVisibility = this.toggleVisibility.bind(this);
        this.searchAction = this.searchAction.bind(this);
    }

    // tslint:disable-next-line:no-any
    public UNSAFE_componentWillReceiveProps(nextProps: MatterMessageViewInnerProps, nextContext: any): void {
        this.setState({ showNewMessage: nextProps.showNewMessage });
    }

    toggleVisibility() {
        this.setState({ showNewMessage: !this.state.showNewMessage });
    }

    searchAction(filter: string) {
        this.setState({ searchQuery: filter });
    }

    messageCompleteAction = (latestMessage: Message) => {
        this.setState({ latestMessage: latestMessage });
    }

    public render() {
        const { showNewMessage } = this.state;
        const { classes } = this.props;

        return (
            <div className={classes.root}>
                <div className={classes.searchRow}>
                    <div className={classes.searchField}>
                        <MatterSearchField
                            hint={MatterTerminology.SearchMessages}
                            search={this.state.searchQuery}
                            searchAction={this.searchAction}
                            accent="#7E22CE"
                        />
                    </div>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddCommentOutlinedIcon fontSize="small" />}
                        onClick={this.toggleVisibility}
                    >
                        {this.props.isUserInternal ? MatterTerminology.NewMessageNotes : MatterTerminology.NewMessage}
                    </Button>
                </div>

                <Collapse in={showNewMessage}>
                    <MessageInputCard
                        id={this.props.id}
                        sourceSystem={this.props.sourceSystem}
                        isUserInternal={this.props.isUserInternal}
                        toggleVisibility={this.toggleVisibility}
                        updateAction={this.messageCompleteAction}
                    />
                </Collapse>

                <div className={classes.streamContainer}>
                    <MessageStream
                        filter={this.state.searchQuery}
                        // tslint:disable-next-line:radix
                        id={parseInt(this.props.id)}
                        message={this.state.latestMessage}
                    />
                </div>
            </div>
        );
    }
}

// Function wrapper so the class above (constructor/lifecycle-heavy, kept as-is rather than
// rewritten as a hook) can still consume makeStyles.
export const RvMatterMessageView: React.FC<MatterMessageViewProps> = (props) => {
    const classes = useStyles();
    return <MatterMessageViewInner {...props} classes={classes} />;
};
